import { useState, useEffect, useCallback, useRef } from 'react';
import './BingoGame.css';

const DEMO_CARD = [
  [12, 18, 31, 44, 68],
  [9, 25, 39, 52, 71],
  [2, 19, 0, 59, 74],
  [7, 21, 47, 63, 75],
  [5, 28, 36, 54, 70],
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const BingoGame = () => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [drawnNumbers, setDrawnNumbers] = useState([]);
  const [cardNumbers, setCardNumbers] = useState([]);
  const [won, setWon] = useState(false);
  const [winningCells, setWinningCells] = useState([]);
  const [lastDrawnNumber, setLastDrawnNumber] = useState(null);
  
  const numberPoolRef = useRef([]);
  const totalScreens = 4;

  // Inicializa o pool de números
  const buildNumberPool = useCallback(() => {
    const numbers = [];
    DEMO_CARD.forEach((row) => {
      row.forEach((num) => {
        numbers.push(num);
      });
    });
    setCardNumbers(numbers);

    const onCard = new Set(numbers.filter((n) => n !== 0));
    const all = Array.from({ length: 75 }, (_, i) => i + 1).filter((n) => !onCard.has(n));
    const shuffled = shuffle(all);
    numberPoolRef.current = shuffled;
  }, []);

  useEffect(() => {
    buildNumberPool();
  }, [buildNumberPool]);

  const isMarked = useCallback(
    (r, c) => {
      const num = DEMO_CARD[r][c];
      return num === 0 || drawnNumbers.includes(num);
    },
    [drawnNumbers]
  );

  const findWinningPattern = useCallback(() => {
    // Linhas
    for (let r = 0; r < 5; r++) {
      if ([0, 1, 2, 3, 4].every((c) => isMarked(r, c))) {
        return [0, 1, 2, 3, 4].map((c) => ({ r, c }));
      }
    }
    // Colunas
    for (let c = 0; c < 5; c++) {
      if ([0, 1, 2, 3, 4].every((r) => isMarked(r, c))) {
        return [0, 1, 2, 3, 4].map((r) => ({ r, c }));
      }
    }
    // Diagonal principal
    if ([0, 1, 2, 3, 4].every((i) => isMarked(i, i))) {
      return [0, 1, 2, 3, 4].map((i) => ({ r: i, c: i }));
    }
    // Diagonal secundária
    if ([0, 1, 2, 3, 4].every((i) => isMarked(i, 4 - i))) {
      return [0, 1, 2, 3, 4].map((i) => ({ r: i, c: 4 - i }));
    }
    return null;
  }, [isMarked]);

  const launchConfetti = useCallback(() => {
    const container = document.getElementById('confetti');
    if (!container) return;

    container.innerHTML = '';
    const colors = ['#f5c518', '#3a8dde', '#22c55e', '#e74c3c', '#a855f7', '#f97316'];

    for (let i = 0; i < 80; i++) {
      const piece = document.createElement('div');
      piece.classList.add('confetti-piece');

      const color = colors[Math.floor(Math.random() * colors.length)];
      const left = Math.random() * 100;
      const delay = Math.random() * 3;
      const dur = 2.5 + Math.random() * 2;
      const size = 8 + Math.random() * 12;

      piece.style.cssText = `
        left: ${left}%;
        background: ${color};
        width: ${size}px;
        height: ${size}px;
        animation-duration: ${dur}s;
        animation-delay: ${delay}s;
      `;

      container.appendChild(piece);
    }
  }, []);

  const drawNumber = useCallback(() => {
    if (won) return;

    const cardRemaining = cardNumbers.filter(
      (n) => n !== 0 && !drawnNumbers.includes(n)
    );

    if (numberPoolRef.current.length === 0 && cardRemaining.length === 0) {
      return;
    }

    let drawn;

    if (cardRemaining.length > 0 && Math.random() < 0.55) {
      const idx = Math.floor(Math.random() * cardRemaining.length);
      drawn = cardRemaining[idx];
    } else if (numberPoolRef.current.length > 0) {
      drawn = numberPoolRef.current.pop();
    } else {
      drawn = cardRemaining[0];
    }

    const newDrawn = [...drawnNumbers, drawn];
    setDrawnNumbers(newDrawn);
    setLastDrawnNumber(drawn);

    // Verifica padrão vencedor
    const pattern = findWinningPattern();
    if (pattern) {
      setWon(true);
      setWinningCells(pattern);
      launchConfetti();
    }
  }, [won, drawnNumbers, cardNumbers, findWinningPattern, launchConfetti]);

  const resetDemo = useCallback(() => {
    setDrawnNumbers([]);
    setWon(false);
    setWinningCells([]);
    setLastDrawnNumber(null);
    buildNumberPool();
  }, [buildNumberPool]);

  const goToScreen = useCallback((index) => {
    if (index < 0 || index >= totalScreens) return;
    setCurrentScreen(index);
    if (index === 3) {
      resetDemo();
    }
  }, [totalScreens, resetDemo]);

  const nextScreen = useCallback(() => {
    goToScreen(currentScreen + 1);
  }, [currentScreen, goToScreen]);

  const prevScreen = useCallback(() => {
    goToScreen(currentScreen - 1);
  }, [currentScreen, goToScreen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          nextScreen();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          prevScreen();
          break;
        case ' ':
          e.preventDefault();
          nextScreen();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nextScreen, prevScreen]);

  return (
    <div className="bingo-game">
      {/* Progresso */}
      <div className="progress-bar">
        {Array.from({ length: totalScreens }).map((_, i) => (
          <button
            key={i}
            className={`dot ${i === currentScreen ? 'active' : ''}`}
            onClick={() => goToScreen(i)}
            aria-label={`Ir para tela ${i + 1}`}
          />
        ))}
      </div>

      {/* Navegação */}
      <nav className="nav-controls">
        <button
          onClick={prevScreen}
          disabled={currentScreen === 0}
          aria-label="Tela anterior"
        >
          &#8592;
        </button>
        <button
          onClick={nextScreen}
          disabled={currentScreen === totalScreens - 1}
          aria-label="Próxima tela"
        >
          &#8594;
        </button>
      </nav>

      {/* TELA 0: Introdução */}
      <section className={`screen ${currentScreen === 0 ? 'active' : ''}`}>
        <div className="screen-content center-all animate-fade-in">
          <div className="intro-icon">🎱</div>
          <h1 className="title-giant">Como funciona o Bingo?</h1>
          <p className="subtitle">Marque os números sorteados e complete o padrão vencedor.</p>
          <button className="btn-primary" onClick={nextScreen}>
            Começar →
          </button>
        </div>
      </section>

      {/* TELA 1: Componentes */}
      <section className={`screen ${currentScreen === 1 ? 'active' : ''}`}>
        <div className="screen-content animate-fade-in">
          <h2 className="title-large">O que você vai usar</h2>
          <div className="components-grid">
            <div className="component-card">
              <div className="card-icon">
                <div className="mini-card">
                  <div className="mini-row">
                    <span>7</span>
                    <span>23</span>
                    <span>41</span>
                  </div>
                  <div className="mini-row">
                    <span>12</span>
                    <span>★</span>
                    <span>55</span>
                  </div>
                  <div className="mini-row">
                    <span>3</span>
                    <span>31</span>
                    <span>69</span>
                  </div>
                </div>
              </div>
              <p className="component-label">Cartela</p>
              <p className="component-desc">Sua grade de números</p>
            </div>

            <div className="component-card">
              <div className="marker-icon">
                <div className="marker-visual">
                  <div className="marker-body"></div>
                  <div className="marker-tip"></div>
                </div>
              </div>
              <p className="component-label">Caneta</p>
              <p className="component-desc">Para marcar os números</p>
            </div>
          </div>
        </div>
      </section>

      {/* TELA 2: Como vencer */}
      <section className={`screen ${currentScreen === 2 ? 'active' : ''}`}>
        <div className="screen-content animate-fade-in">
          <h2 className="title-large">Como vencer</h2>
          <div className="victory-grid">
            {/* Linha */}
            <div className="victory-card" id="vc-row">
              <p className="victory-label">Linha</p>
              <div className="mini-bingo mb-row-scan">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="mb-row" style={{ '--k': row }}>
                    {[0, 1, 2, 3, 4].map((col) => (
                      <span key={`${row}-${col}`} className="mb-cell"></span>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Coluna */}
            <div className="victory-card" id="vc-col">
              <p className="victory-label">Coluna</p>
              <div className="mini-bingo mb-col-scan">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="mb-row">
                    {[0, 1, 2, 3, 4].map((col) => (
                      <span
                        key={`${row}-${col}`}
                        className="mb-cell"
                        style={{ '--k': col }}
                      ></span>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Diagonal */}
            <div className="victory-card" id="vc-diag">
              <p className="victory-label">Diagonal</p>
              <div className="mini-bingo">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="mb-row">
                    {[0, 1, 2, 3, 4].map((col) => {
                      const isDiagMain = row === col;
                      const isDiagSecond = row === 4 - col;
                      const isCenter = row === 2 && col === 2;

                      return (
                        <span
                          key={`${row}-${col}`}
                          className={`mb-cell ${isDiagMain || isDiagSecond ? 'diag' : ''} ${
                            isCenter ? 'diag-center' : ''
                          }`}
                          style={{ '--k': isDiagMain ? 0 : 1 }}
                        ></span>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Cartela cheia */}
            <div className="victory-card" id="vc-full">
              <p className="victory-label">Cartela cheia</p>
              <div className="mini-bingo">
                {[0, 1, 2, 3, 4].map((row) => (
                  <div key={row} className="mb-row">
                    {[0, 1, 2, 3, 4].map((col) => (
                      <span key={`${row}-${col}`} className="mb-cell win"></span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TELA 3: Demonstração */}
      <section className={`screen ${currentScreen === 3 ? 'active' : ''}`}>
        <div className="screen-content animate-fade-in">
          <h2 className="title-large">Vamos simular!</h2>

          {lastDrawnNumber !== null && (
            <div className="drawn-display">
              <span className="drawn-label">Número sorteado:</span>
              <span key={lastDrawnNumber} className="drawn-number">
                {lastDrawnNumber}
              </span>
            </div>
          )}

          <div className="bingo-card">
            <div className="bingo-header">
              <span>B</span>
              <span>I</span>
              <span>N</span>
              <span>G</span>
              <span>O</span>
            </div>
            <div className="bingo-body">
              {DEMO_CARD.map((row, r) =>
                row.map((num, c) => {
                  const isWinningCell = winningCells.some((cell) => cell.r === r && cell.c === c);
                  const isMarkedCell = num === 0 || drawnNumbers.includes(num);
                  const wasHighlighted = lastDrawnNumber === num;

                  return (
                    <div
                      key={`${r}-${c}`}
                      className={`bingo-cell ${num === 0 ? 'free marked' : ''} ${
                        isMarkedCell ? 'marked' : ''
                      } ${wasHighlighted ? 'highlight' : ''} ${
                        isWinningCell ? 'winning-line' : ''
                      }`}
                      data-row={r}
                      data-col={c}
                      data-num={num}
                    >
                      {num === 0 ? 'FREE' : num}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {won && (
            <div className="win-banner">
              <p className="finale-instruction">Você completou o padrão! Levante a mão e grite:</p>
              <div className="bingo-shout">BINGO!</div>
            </div>
          )}

          <button className="btn-primary" onClick={drawNumber} disabled={won}>
            Sortear próximo número 🎲
          </button>
          <button className="btn-secondary" onClick={resetDemo}>
            Reiniciar demonstração
          </button>
        </div>
        <div className="confetti-container" id="confetti"></div>
      </section>
    </div>
  );
};

export default BingoGame;
